/**
 * Lectura y análisis del Excel de Generar Fichas de Pago: qué alumno, qué concepto, cuánto,
 * y qué va a pasar con cada fila.
 *
 * Sin React ni antd a propósito: se puede probar con un `node script.js`.
 *
 * El total que se muestra aquí es una estimación para la vista previa. El definitivo lo
 * calcula el servidor al generar la ficha, con las retenciones reales del alumno.
 */

import * as XLSX from "xlsx";
import {
  texto,
  normalizarTexto,
  llaveClave,
  registrarLlave,
  buscarEnIndices,
  parsearFechaExcel,
} from "../../../../../Components/importarAsignaciones.utils";

/**
 * Fuente única de las columnas: de aquí salen la detección de encabezados y las hojas de la
 * plantilla. `prefijo` va normalizado (sin acentos, en MAYÚSCULAS); una columna se reconoce
 * si su encabezado EMPIEZA con él.
 */
export const COLUMNAS_ESPERADAS = [
  {
    clave: "matricula",
    prefijo: "MATRICULA",
    etiqueta: "MATRICULA",
    ancho: 18,
    ejemplo: "EJEMPLO-001",
    ayuda: "Matrícula del alumno, tal como está en su ficha de Escuela → Alumnos. Es lo único con lo que se le identifica.",
  },
  {
    clave: "concepto",
    prefijo: "CONCEPTO",
    etiqueta: "CONCEPTO",
    ancho: 22,
    ejemplo: "COLEGIATURA",
    soloConcepto: true,
    ayuda:
      'Clave del insumo que se cobra (ver la hoja "Conceptos"). También se acepta el código o la descripción exacta. Tiene que estar marcado "de venta". Se ignora si se cobra la lista escolar.',
  },
  {
    clave: "monto",
    prefijo: "MONTO",
    etiqueta: "MONTO",
    ancho: 14,
    ejemplo: "2500.00",
    soloConcepto: true,
    ayuda:
      "Total a pagar CON impuestos: lo que el alumno va a transferir. Si se deja vacío se usa el precio de lista del alumno más impuestos. Se ignora si se cobra la lista escolar.",
  },
  {
    clave: "fechaLimite",
    prefijo: "FECHA LIMITE",
    etiqueta: "FECHA LIMITE",
    ancho: 16,
    ejemplo: "15/11/2026",
    ayuda: "Opcional. Se imprime en la ficha y en el correo; la ficha NO se cancela al pasar la fecha. Escríbela como texto DD/MM/AAAA.",
  },
  {
    clave: "periodo",
    prefijo: "PERIODO",
    etiqueta: "PERIODO",
    ancho: 16,
    ejemplo: "NOV-2026",
    soloConcepto: true,
    ayuda:
      "Opcional, pero recomendado en cargos que se repiten (colegiaturas). Con periodo, al alumno no se le puede generar dos veces el mismo concepto del mismo periodo; sin periodo, sólo se evita tener dos fichas del mismo concepto SIN pagar. Se ignora si se cobra la lista escolar.",
  },
  {
    clave: "observaciones",
    prefijo: "OBSERVACION",
    etiqueta: "OBSERVACIONES",
    ancho: 30,
    ejemplo: "",
    ayuda: "Opcional. Se guarda en las observaciones de la venta.",
  },
];

// Qué se cobra en cada ficha: lo que diga el archivo, o lo pendiente de las listas escolares
// del alumno (la de su grupo y la individual, como "Compra Directa" de la tienda).
export const MODOS = {
  concepto: { etiqueta: "Lo que diga el archivo", obligatorias: ["matricula", "concepto"] },
  lista: { etiqueta: "La lista escolar del alumno", obligatorias: ["matricula"] },
};

export const COLUMNAS_OBLIGATORIAS = MODOS.concepto.obligatorias;

export const ESTADOS = {
  crear: { etiqueta: "Se genera", color: "green" },
  duplicada: { etiqueta: "Ya tiene ficha", color: "gold" },
  error: { etiqueta: "No se puede", color: "red" },
  creada: { etiqueta: "Ficha generada", color: "green" },
  detenida: { etiqueta: "No se procesó (detenida)", color: "default" },
};

export const etiquetaEstado = (estado) => ESTADOS[estado]?.etiqueta || estado || "";
export const colorEstado = (estado) => ESTADOS[estado]?.color || "default";

const MESES = ["ENE", "FEB", "MAR", "ABR", "MAY", "JUN", "JUL", "AGO", "SEP", "OCT", "NOV", "DIC"];

// Serial de Excel a partir del cual un número en la columna PERIODO se lee como fecha
// (46000 ≈ 2025-12). Excel convierte "NOV-2026" escrito a mano en una fecha, y el archivo
// se lee sin `cellDates`, así que llega como serial.
const SERIAL_FECHA_MINIMO = 20000;

const redondear2 = (numero) => Math.round((Number(numero) + Number.EPSILON) * 100) / 100;

export const detectarColumnas = (filas, modo = "concepto") => {
  const obligatorias = (MODOS[modo] || MODOS.concepto).obligatorias;
  const llaves = new Set();
  (filas || []).forEach((fila) => {
    Object.keys(fila).forEach((llave) => {
      if (!llave.startsWith("__")) llaves.add(llave);
    });
  });

  const columnas = {};
  COLUMNAS_ESPERADAS.forEach((esperada) => {
    const encontrada = [...llaves].find((llave) => normalizarTexto(llave).startsWith(esperada.prefijo));
    if (encontrada) columnas[esperada.clave] = encontrada;
  });

  const faltantes = obligatorias.filter((clave) => !columnas[clave]);

  return {
    columnas,
    faltantes,
    etiquetasFaltantes: COLUMNAS_ESPERADAS.filter((columna) => faltantes.includes(columna.clave))
      .map((columna) => columna.etiqueta)
      .join(", "),
  };
};

/**
 * "$2,500.00", "2500", 2500 → 2500. Vacío → null. Lo que no se entienda → NaN.
 */
export const parsearMonto = (valor) => {
  if (valor === undefined || valor === null || valor === "") return null;
  if (typeof valor === "number") return Number.isFinite(valor) ? redondear2(valor) : NaN;
  const limpio = texto(valor).replace(/[$\s]/g, "").replace(/,/g, "");
  if (!limpio) return null;
  if (!/^-?\d+(\.\d+)?$/.test(limpio)) return NaN;
  return redondear2(Number(limpio));
};

export const parsearPeriodo = (valor) => {
  if (valor === undefined || valor === null || valor === "") return "";
  if (typeof valor === "number" && valor >= SERIAL_FECHA_MINIMO) {
    const partes = XLSX.SSF.parse_date_code(valor);
    if (partes) return `${MESES[partes.m - 1]}-${partes.y}`;
  }
  return texto(valor);
};

export const nombreAlumno = (alumno) => {
  if (!alumno) return "";
  const razon = texto(alumno.razonSocial);
  const fisica = [alumno.nombre, alumno.apellidoPaterno, alumno.apellidoMaterno].map(texto).filter(Boolean).join(" ");
  const base = alumno.persona ? razon || fisica : fisica || razon;
  return alumno.nombreComercial ? `${texto(alumno.nombreComercial)} - ${base}` : base;
};

export const correosDeAlumno = (alumno) => {
  const vistos = new Set();
  const limpios = [];
  [alumno?.correo, ...(alumno?.correosAdicionales || [])].forEach((valueCorreo) => {
    const correo = texto(valueCorreo);
    if (!correo || vistos.has(correo.toLowerCase())) return;
    vistos.add(correo.toLowerCase());
    limpios.push(correo);
  });
  return limpios;
};

/**
 * Índices de cruce. Una llave que le queda a dos registros se marca ambigua (null): mejor
 * pedir que se corrija el archivo que cobrarle al alumno equivocado.
 */
export const construirIndices = (catalogos) => {
  const alumnosPorMatricula = new Map();
  const alumnosPorDigitos = new Map();
  (catalogos?.alumnos || []).forEach((alumno) => {
    const llave = llaveClave(alumno.matricula);
    registrarLlave(alumnosPorMatricula, llave, alumno);
    const digitos = llave.replace(/[^0-9]/g, "").replace(/^0+/, "");
    registrarLlave(alumnosPorDigitos, digitos, alumno);
  });

  const conceptosPorClave = new Map();
  const conceptosPorCodigo = new Map();
  const conceptosPorDescripcion = new Map();
  (catalogos?.conceptos || []).forEach((concepto) => {
    registrarLlave(conceptosPorClave, llaveClave(concepto.clave), concepto);
    if (concepto.codigo !== undefined && concepto.codigo !== null) {
      registrarLlave(conceptosPorCodigo, String(concepto.codigo), concepto);
    }
    registrarLlave(conceptosPorDescripcion, normalizarTexto(concepto.descripcion), concepto);
  });

  return {
    alumnosPorMatricula,
    alumnosPorDigitos,
    conceptosPorClave,
    conceptosPorCodigo,
    conceptosPorDescripcion,
    conceptosPorId: new Map((catalogos?.conceptos || []).map((concepto) => [String(concepto._id), concepto])),
    // Listas ESCOLARES (paquetes). `listasPorId`, más abajo, son las listas de PRECIOS.
    listasEscolaresPorId: new Map((catalogos?.listas || []).map((lista) => [String(lista._id), lista])),
    escuelasPorId: new Map((catalogos?.escuelas || []).map((escuela) => [String(escuela._id), escuela])),
    gruposPorId: new Map((catalogos?.grupos || []).map((grupo) => [String(grupo._id), grupo])),
    tiposPorId: new Map((catalogos?.tiposDeCliente || []).map((tipo) => [String(tipo._id), tipo])),
    listasPorId: new Map((catalogos?.listasPrecio || []).map((lista) => [String(lista._id), lista.nombre])),
  };
};

/**
 * Matrícula exacta (sin espacios ni guiones); si no, sólo los dígitos sin ceros a la
 * izquierda —Excel se come los ceros de "00123" si la celda es número—, y eso sólo si le
 * queda a UN alumno.
 */
export const buscarAlumno = (indices, matricula) => {
  const llave = llaveClave(matricula);
  if (!llave) return { alumno: null, motivo: "está vacía" };
  const { encontrado, ambiguo } = buscarEnIndices(
    [indices.alumnosPorMatricula, indices.alumnosPorDigitos],
    [llave, llave.replace(/[^0-9]/g, "").replace(/^0+/, "")],
  );
  if (ambiguo) return { alumno: null, motivo: `"${texto(matricula)}" le queda a más de un alumno` };
  if (!encontrado) return { alumno: null, motivo: `"${texto(matricula)}" no existe en Alumnos` };
  return { alumno: encontrado };
};

export const buscarConcepto = (indices, valor) => {
  const valorTexto = texto(valor);
  if (!valorTexto) return { concepto: null, motivo: "está vacío" };
  const { encontrado, ambiguo } = buscarEnIndices(
    [indices.conceptosPorClave, indices.conceptosPorCodigo, indices.conceptosPorDescripcion],
    [llaveClave(valorTexto), /^\d+$/.test(valorTexto) ? valorTexto : "", normalizarTexto(valorTexto)],
  );
  if (ambiguo) return { concepto: null, motivo: `"${valorTexto}" le queda a más de un insumo; usa la clave` };
  if (!encontrado) {
    return { concepto: null, motivo: `"${valorTexto}" no existe o no es de venta (revisa la hoja "Conceptos")` };
  }
  return { concepto: encontrado };
};

/**
 * Precio de lista del concepto para el alumno: la primera lista candidata (misma prioridad
 * que el servidor) en la que tenga precio.
 */
export const precioDeLista = (indices, alumno, concepto) => {
  const tipo = indices.tiposPorId.get(String(alumno?.tipoClienteId));
  const candidatos = [alumno?.precioOnlineId, tipo?.precioOnlineId, alumno?.precioContadoId, tipo?.precioContadoId]
    .filter(Boolean)
    .map(String);
  for (const listaId of candidatos) {
    const entrada = (concepto?.precios || []).find((valuePrecio) => String(valuePrecio.precioId) === listaId);
    if (entrada && entrada.precio !== undefined && entrada.precio !== null) {
      return { precio: Number(entrada.precio), listaNombre: indices.listasPorId.get(listaId) || "" };
    }
  }
  return null;
};

/**
 * Igual que precioDeLista pero para varios conceptos: la primera lista candidata en la que
 * TODOS tengan precio (mismo criterio que resolverPrecioLibros del servidor).
 */
export const preciosDeListaParaTodos = (indices, alumno, conceptos) => {
  const tipo = indices.tiposPorId.get(String(alumno?.tipoClienteId));
  const candidatos = [alumno?.precioOnlineId, tipo?.precioOnlineId, alumno?.precioContadoId, tipo?.precioContadoId]
    .filter(Boolean)
    .map(String);
  for (const listaId of candidatos) {
    const precios = conceptos.map((concepto) => {
      const entrada = (concepto?.precios || []).find((valuePrecio) => String(valuePrecio.precioId) === listaId);
      return entrada && entrada.precio !== undefined && entrada.precio !== null ? Number(entrada.precio) : null;
    });
    if (precios.every((precio) => precio !== null)) {
      return { precios, listaNombre: indices.listasPorId.get(listaId) || "" };
    }
  }
  return null;
};

/** Listas escolares del alumno con el criterio de la tienda: la de su grupo Y la individual. */
export const listasDelAlumno = (indices, alumno) => {
  const ids = [indices.gruposPorId.get(String(alumno?.grupoEscuelaId))?.listaId, alumno?.listaId]
    .filter(Boolean)
    .map(String);
  return [...new Set(ids)].map((id) => indices.listasEscolaresPorId.get(id)).filter(Boolean);
};

const leer = (fila, columna) => (columna ? fila?.[columna] : undefined);

/**
 * Cada renglón del archivo con el alumno y el concepto ya resueltos.
 */
export const lecturaDeFilas = ({ filas, columnas, indices, modo = "concepto" }) =>
  (filas || []).map((fila, indice) => {
    const esLista = modo === "lista";
    const numeroFila = (fila["__rowNum__"] ?? indice) + 1;
    const matricula = texto(leer(fila, columnas.matricula));
    // En modo lista lo que se cobra sale de las listas del alumno: concepto, monto y periodo
    // del archivo se ignoran aunque vengan llenos.
    const conceptoTexto = esLista ? "" : texto(leer(fila, columnas.concepto));
    const montoCrudo = esLista ? undefined : leer(fila, columnas.monto);
    const fechaCruda = leer(fila, columnas.fechaLimite);
    const periodo = esLista ? "" : parsearPeriodo(leer(fila, columnas.periodo));
    const observaciones = texto(leer(fila, columnas.observaciones));

    const errores = [];

    const resultadoAlumno = buscarAlumno(indices, matricula);
    if (!resultadoAlumno.alumno) errores.push(`Matrícula: ${resultadoAlumno.motivo}`);

    const resultadoConcepto = esLista ? { concepto: null } : buscarConcepto(indices, conceptoTexto);
    if (!esLista && !resultadoConcepto.concepto) errores.push(`Concepto: ${resultadoConcepto.motivo}`);

    const monto = parsearMonto(montoCrudo);
    if (Number.isNaN(monto)) errores.push(`Monto: "${texto(montoCrudo)}" no es un número`);
    else if (monto !== null && monto <= 0) errores.push("Monto: tiene que ser mayor que cero");

    const fechaLimite = parsearFechaExcel(fechaCruda);
    if (fechaCruda !== undefined && fechaCruda !== null && texto(fechaCruda) !== "" && !fechaLimite) {
      errores.push(`Fecha límite: "${texto(fechaCruda)}" no es una fecha (usa DD/MM/AAAA)`);
    }

    return {
      indice,
      numeroFila,
      matricula,
      conceptoTexto,
      monto: Number.isNaN(monto) ? null : monto,
      fechaLimite,
      periodo,
      observaciones,
      alumno: resultadoAlumno.alumno,
      concepto: resultadoConcepto.concepto,
      vacia: !matricula && !conceptoTexto && !(esLista && observaciones),
      errores,
    };
  });

/**
 * Llaves para preguntarle al servidor qué fichas activas ya existen. POSICIONAL respecto a
 * `lecturas`: null en las filas que no tienen alumno y concepto resueltos.
 */
export const llavesParaRevision = (lecturas) =>
  lecturas.map((lectura) =>
    lectura.alumno && lectura.concepto
      ? { clienteId: String(lectura.alumno._id), insumoId: String(lectura.concepto._id), periodo: lectura.periodo }
      : null,
  );

/**
 * Lo que el importador genérico espera por fila: `{ok, accion, datos}`.
 *
 * Reglas que dependen del archivo completo, y que por eso viven aquí:
 *  - Una fila repetida (mismo alumno, concepto y periodo que una anterior del archivo) no se
 *    genera dos veces.
 *  - Las que ya tienen ficha activa en el sistema (`existentes`, del servidor) tampoco.
 *
 * @param {Array} existentes  posicional: {duplicada, folio, pagada} | null
 * @returns {Map<number, Object>} indice → decisión
 */
export const analizarArchivo = ({ lecturas, existentes, indices, bloqueado }) => {
  const decisiones = new Map();
  const primeraFilaPorLlave = new Map();

  lecturas.forEach((lectura) => {
    if (lectura.vacia) {
      decisiones.set(lectura.indice, { ok: false, errores: [{ campo: "Fila", motivo: "está vacía" }] });
      return;
    }

    const alumno = lectura.alumno;
    const concepto = lectura.concepto;
    const escuela = alumno ? indices.escuelasPorId.get(String(alumno.escuelaId)) : null;
    const grupo = alumno ? indices.gruposPorId.get(String(alumno.grupoEscuelaId)) : null;
    const correos = correosDeAlumno(alumno);

    const datos = {
      numeroFila: lectura.numeroFila,
      matricula: lectura.matricula,
      clienteId: alumno ? String(alumno._id) : "",
      alumno: nombreAlumno(alumno),
      escuela: escuela?.nombre || "",
      grupo: grupo?.nombre || "",
      insumoId: concepto ? String(concepto._id) : "",
      concepto: lectura.conceptoTexto,
      conceptoNombre: concepto?.descripcion || "",
      monto: lectura.monto,
      fechaLimite: lectura.fechaLimite,
      periodo: lectura.periodo,
      observaciones: lectura.observaciones,
      destinatarios: correos.join(", "),
      totalEstimado: null,
      origenPrecio: "",
      estadoPrevisto: "crear",
      motivo: "",
      aviso: "",
    };

    const problemas = [...lectura.errores];

    if (bloqueado) problemas.unshift(bloqueado);

    if (alumno && !alumno.escuelaId) problemas.push("El alumno no tiene escuela asignada");
    else if (alumno && !escuela) problemas.push("No se encontró la escuela del alumno");
    else if (escuela?.problema) problemas.push(escuela.problema);

    if (concepto?.problema) problemas.push(concepto.problema);

    if (alumno && concepto && problemas.length === 0) {
      if (lectura.monto !== null) {
        datos.totalEstimado = lectura.monto;
        datos.origenPrecio = "Monto del archivo";
      } else {
        const lista = precioDeLista(indices, alumno, concepto);
        if (!lista) {
          problemas.push("El MONTO viene vacío y el concepto no tiene precio en las listas del alumno");
        } else if (!(lista.precio > 0)) {
          problemas.push(`El precio en la lista ${lista.listaNombre} es cero; captura el MONTO`);
        } else {
          const factor = concepto.factorTotal || { a: 1, b: 0 };
          datos.totalEstimado = redondear2(factor.a * lista.precio + factor.b);
          datos.origenPrecio = `Lista ${lista.listaNombre}`.trim();
        }
      }
    }

    if (problemas.length > 0) {
      datos.estadoPrevisto = "error";
      datos.motivo = problemas.join(" | ");
      decisiones.set(lectura.indice, { ok: true, accion: "error", datos });
      return;
    }

    if (concepto.inventariable && !concepto.servicio) {
      datos.aviso = "El concepto es inventariable: la venta queda pendiente de entregar";
    }

    const llave = `${datos.clienteId}|${datos.insumoId}|${normalizarTexto(datos.periodo)}`;
    if (primeraFilaPorLlave.has(llave)) {
      datos.estadoPrevisto = "duplicada";
      datos.motivo = `Repetida en el archivo: mismo alumno, concepto y periodo que la fila ${primeraFilaPorLlave.get(llave)}`;
      decisiones.set(lectura.indice, { ok: true, accion: "sinCambios", datos });
      return;
    }
    primeraFilaPorLlave.set(llave, lectura.numeroFila);

    const existente = existentes?.[lectura.indice];
    if (existente?.duplicada) {
      datos.estadoPrevisto = "duplicada";
      datos.motivo = datos.periodo
        ? `Ya tiene la ficha ${existente.folio}${existente.pagada ? " (pagada)" : ""} de este concepto para el periodo ${normalizarTexto(datos.periodo)}`
        : `Ya tiene la ficha ${existente.folio} de este concepto sin pagar`;
      decisiones.set(lectura.indice, { ok: true, accion: "sinCambios", datos });
      return;
    }

    decisiones.set(lectura.indice, { ok: true, accion: "crear", datos });
  });

  return decisiones;
};

/**
 * Modo lista: por fila, lo que se le cobraría al alumno = los artículos de sus listas (la de
 * su grupo y la individual) que todavía no tiene en una venta no cancelada.
 *
 * Una ficha por alumno: si la matrícula viene dos veces, la segunda no se genera.
 *
 * @param {Object} compradas  { [clienteId]: ["listaId|insumoId", ...] }, del servidor
 * @returns {Map<number, Object>} indice → decisión
 */
export const analizarArchivoLista = ({ lecturas, compradas, indices, bloqueado }) => {
  const decisiones = new Map();
  const primeraFilaPorAlumno = new Map();

  lecturas.forEach((lectura) => {
    if (lectura.vacia) {
      decisiones.set(lectura.indice, { ok: false, errores: [{ campo: "Fila", motivo: "está vacía" }] });
      return;
    }

    const alumno = lectura.alumno;
    const escuela = alumno ? indices.escuelasPorId.get(String(alumno.escuelaId)) : null;
    const grupo = alumno ? indices.gruposPorId.get(String(alumno.grupoEscuelaId)) : null;
    const listas = alumno ? listasDelAlumno(indices, alumno) : [];

    const datos = {
      numeroFila: lectura.numeroFila,
      matricula: lectura.matricula,
      clienteId: alumno ? String(alumno._id) : "",
      alumno: nombreAlumno(alumno),
      escuela: escuela?.nombre || "",
      grupo: grupo?.nombre || "",
      fechaLimite: lectura.fechaLimite,
      observaciones: lectura.observaciones,
      destinatarios: correosDeAlumno(alumno).join(", "),
      conceptoNombre: listas.length ? `Lista escolar: ${listas.map((lista) => lista.nombre).join(" + ")}` : "",
      articulos: 0,
      detalleArticulos: "",
      totalEstimado: null,
      origenPrecio: "",
      estadoPrevisto: "crear",
      motivo: "",
      aviso: "",
    };

    const problemas = [...lectura.errores];
    if (bloqueado) problemas.unshift(bloqueado);

    if (alumno && !alumno.escuelaId) problemas.push("El alumno no tiene escuela asignada");
    else if (alumno && !escuela) problemas.push("No se encontró la escuela del alumno");
    else if (escuela?.problema) problemas.push(escuela.problema);

    if (alumno && listas.length === 0) {
      problemas.push(
        alumno.grupoEscuelaId
          ? "El alumno no tiene lista individual y su grupo no tiene lista asignada (o está deshabilitada)"
          : "El alumno no tiene lista individual ni grupo escolar con lista",
      );
    }

    if (alumno && problemas.length === 0) {
      if (primeraFilaPorAlumno.has(datos.clienteId)) {
        datos.estadoPrevisto = "duplicada";
        datos.motivo = `Repetida en el archivo: el alumno ya viene en la fila ${primeraFilaPorAlumno.get(datos.clienteId)}`;
        decisiones.set(lectura.indice, { ok: true, accion: "sinCambios", datos });
        return;
      }
      primeraFilaPorAlumno.set(datos.clienteId, lectura.numeroFila);

      const yaTiene = new Set(compradas?.[datos.clienteId] || []);
      const pendientes = [];
      listas.forEach((lista) =>
        (lista.insumos || []).forEach((item) => {
          if (!item?.insumoId || yaTiene.has(`${lista._id}|${item.insumoId}`)) return;
          pendientes.push({
            lista,
            concepto: indices.conceptosPorId.get(String(item.insumoId)),
            cantidad: Number(item.cantidad) > 0 ? Number(item.cantidad) : 1,
          });
        }),
      );

      if (pendientes.length === 0) {
        datos.estadoPrevisto = "duplicada";
        datos.motivo = "Ya tiene todos los artículos de su lista (comprados o en una ficha pendiente)";
        decisiones.set(lectura.indice, { ok: true, accion: "sinCambios", datos });
        return;
      }

      const sinConcepto = pendientes.find((pendiente) => !pendiente.concepto);
      const conProblema = pendientes.find((pendiente) => pendiente.concepto?.problema);
      if (sinConcepto) {
        problemas.push(`Un artículo de la lista "${sinConcepto.lista.nombre}" no es de venta o está deshabilitado`);
      } else if (conProblema) {
        problemas.push(`Lista "${conProblema.lista.nombre}": ${conProblema.concepto.problema}`);
      } else {
        const lista = preciosDeListaParaTodos(
          indices,
          alumno,
          pendientes.map((pendiente) => pendiente.concepto),
        );
        if (!lista) {
          problemas.push("Ninguna lista de precios del alumno tiene precio para todos los artículos de su lista");
        } else {
          datos.articulos = pendientes.length;
          datos.detalleArticulos = pendientes
            .map((pendiente) => `${pendiente.cantidad} x ${pendiente.concepto.descripcion}`)
            .join(", ");
          datos.totalEstimado = redondear2(
            pendientes.reduce((acc, pendiente, indice) => {
              const factor = pendiente.concepto.factorTotal || { a: 1, b: 0 };
              return acc + redondear2(pendiente.cantidad * (factor.a * lista.precios[indice] + factor.b));
            }, 0),
          );
          datos.origenPrecio = `Lista ${lista.listaNombre}`.trim();
          if (pendientes.some((pendiente) => pendiente.concepto.inventariable && !pendiente.concepto.servicio)) {
            datos.aviso = "Incluye artículos físicos: la venta queda pendiente de entregar";
          }
        }
      }
    }

    if (problemas.length > 0) {
      datos.estadoPrevisto = "error";
      datos.motivo = problemas.join(" | ");
      decisiones.set(lectura.indice, { ok: true, accion: "error", datos });
      return;
    }

    decisiones.set(lectura.indice, { ok: true, accion: "crear", datos });
  });

  return decisiones;
};
